/**
 * Team & Access → Users (`user.view`). Add accounts (`user.create`) that sign in
 * with Microsoft (Entra ID) or, Local, with a password; change name
 * (`user.update`), roles (`user.role.change`) or Source (`user.source.change`),
 * reset a Local account's password (`user.password.reset`), and deactivate /
 * reactivate with the Status switch (`user.status.change`). Each control shows
 * only with its permission. Accounts are never deleted, so AI usage stays attributed.
 *
 * The toolbar, a plain-header table and the pagination, under the page's tab
 * bar. The list is small, so search, filters, sorting and paging run in the
 * browser.
 */

import { Search, UserPlus, Users as UsersIcon } from 'lucide-react';

import type { AuthSource, Role } from '@/api/types';
import { ConfirmDialog } from '@/ui/ConfirmDialog';
import { ROLES, roleLabel, sourceLabel } from '@/ui/format';
import { MultiSelect } from '@/ui/MultiSelect';
import { Pagination } from '@/ui/Pagination';
import { SortHeader } from '@/ui/SortHeader';
import { LoadFailure, LoadingCard } from '@/ui/StateCard';

import { ResetPasswordDialog, UserDialog } from './UserDialogs';
import { UserRow } from './UserRow';
import { type UsersTabState, useUsersTab } from './useUsersTab';
import { NO_ROLE } from './usersList';

const SOURCES: AuthSource[] = ['entra', 'local'];
const FILTER_ROLES: (Role | null)[] = [...ROLES, null];
const STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Deactivated' },
];

export function UsersTab() {
  const state = useUsersTab();
  const { users, update, dialog, visible } = state;

  if (users.isPending) return <LoadingCard label="Loading accounts" />;
  if (users.error) return <LoadFailure error={users.error} onRetry={() => void users.refetch()} />;

  return (
    <>
      <UsersToolbar state={state} />

      {update.error && !dialog && (
        <div className="alert alert-warning form-alert" role="alert">
          <div className="a-body">{update.error.message}</div>
        </div>
      )}

      <UsersTable state={state} />
      {visible.length > 0 && (
        <Pagination
          page={state.page}
          pageSize={state.pageSize}
          total={visible.length}
          noun="users"
          onPage={state.setPage}
          onPageSize={state.setPageSize}
        />
      )}

      <UsersDialogs state={state} />
    </>
  );
}

function UsersToolbar({ state }: { state: UsersTabState }) {
  const { rows, filters } = state;
  const roleCount = (role: Role | null) =>
    rows.filter((u) => (role === null ? u.roles.length === 0 : u.roles.includes(role))).length;
  const sourceCount = (source: AuthSource) => rows.filter((u) => u.auth_source === source).length;
  const activeCount = rows.filter((u) => u.is_active).length;
  const statusCount: Record<string, number> = { active: activeCount, inactive: rows.length - activeCount };

  return (
    <div className="toolbar filter-panel">
      <div className="search-field">
        <Search aria-hidden="true" />
        <input
          type="search"
          value={filters.search}
          placeholder="Search by name, e-mail or UPN"
          aria-label="Search by name, e-mail or UPN"
          onChange={(e) => filters.setSearch(e.target.value)}
        />
      </div>
      <MultiSelect
        label="Filter by source"
        allLabel="All sources"
        noun="sources"
        options={SOURCES.map((s) => ({ value: s, label: sourceLabel(s), meta: sourceCount(s) }))}
        value={filters.sources}
        onChange={filters.setSources}
      />
      <MultiSelect
        label="Filter by role"
        allLabel="All roles"
        noun="roles"
        options={FILTER_ROLES.map((r) => ({ value: r ?? NO_ROLE, label: r === null ? 'No role' : roleLabel(r), meta: roleCount(r) }))}
        value={filters.roles}
        onChange={filters.setRoles}
      />
      <MultiSelect
        label="Filter by status"
        allLabel="All statuses"
        noun="statuses"
        options={STATUSES.map((s) => ({ ...s, meta: statusCount[s.value] }))}
        value={filters.statuses}
        onChange={filters.setStatuses}
      />
      {state.perms.canCreate && (
        <button type="button" className="btn btn-primary" onClick={() => state.setDialog({ kind: 'add' })}>
          <UserPlus aria-hidden="true" /> Add user
        </button>
      )}
    </div>
  );
}

function UsersTable({ state }: { state: UsersTabState }) {
  const { sort, onSort, perms } = state;
  return (
    <div className="table-scroll">
      <table className="data zebra compact">
        <thead>
          <tr>
            <SortHeader label="Name" sortKey="display_name" sort={sort.key} order={sort.order} onSort={onSort} />
            <SortHeader label="E-mail" sortKey="email" sort={sort.key} order={sort.order} onSort={onSort} />
            <SortHeader label="Source" sortKey="auth_source" sort={sort.key} order={sort.order} onSort={onSort} />
            <SortHeader label="Role" sortKey="role" sort={sort.key} order={sort.order} onSort={onSort} />
            <SortHeader label="Status" sortKey="status" sort={sort.key} order={sort.order} onSort={onSort} />
            <SortHeader label="Member since" sortKey="created_at" sort={sort.key} order={sort.order} onSort={onSort} />
            <SortHeader label="Last sign-in" sortKey="last_login_at" sort={sort.key} order={sort.order} onSort={onSort} />
            {perms.withActions && <th scope="col"><span className="sr-only">Actions</span></th>}
          </tr>
        </thead>
        <tbody>
          <UsersRows state={state} />
        </tbody>
      </table>
    </div>
  );
}

function UsersRows({ state }: { state: UsersTabState }) {
  const { pageRows, perms } = state;
  if (pageRows.length === 0) {
    return (
      <tr>
        <td colSpan={perms.withActions ? 8 : 7}>
          <div className="empty">
            <div className="e-icon"><UsersIcon aria-hidden="true" /></div>
            <h3>No accounts match</h3>
            <p>Clear the search, the source, the role or the status filter to see the rest.</p>
          </div>
        </td>
      </tr>
    );
  }
  return (
    <>
      {pageRows.map((u) => (
        <UserRow
          key={u.id}
          u={u}
          isCaller={u.id === state.callerId}
          perms={perms}
          pending={state.update.isPending}
          onToggleActive={state.onToggleActive}
          onDialog={state.setDialog}
        />
      ))}
    </>
  );
}

function UsersDialogs({ state }: { state: UsersTabState }) {
  const { dialog, setDialog, update, callerId } = state;
  const close = () => setDialog(null);
  switch (dialog?.kind) {
    case 'add':
      return <UserDialog key="add" onClose={close} />;
    case 'edit':
      return <UserDialog key="edit" user={dialog.user} isCaller={dialog.user.id === callerId} onClose={close} />;
    case 'password':
      return <ResetPasswordDialog user={dialog.user} onClose={close} />;
    case 'deactivate':
      return (
        <ConfirmDialog
          title="Deactivate this account?"
          confirmLabel="Deactivate"
          danger
          pending={update.isPending}
          error={update.error}
          onClose={() => {
            update.reset();
            close();
          }}
          onConfirm={() => update.mutate({ id: dialog.user.id, change: { is_active: false } }, { onSuccess: close })}
        >
          <p>
            <b>{dialog.user.display_name}</b> is signed out and cannot sign in until reactivated. Nothing is deleted; their AI
            usage stays attributed to them.
          </p>
        </ConfirmDialog>
      );
    default:
      return null;
  }
}
